<template>
    <PageHeader :title="t('bindUsdt.title')" />
    <div class="page-wrapper" style="padding-top: calc(50px * 1.1)!important;">
        <div class="form-section">
            <div class="form-card">
                <div class="card-header">
                    <span class="card-title">{{ t('bindUsdt.cardTitle') }}</span>
                    <span class="card-subtitle">{{ t('bindUsdt.cardSubtitle') }}</span>
                </div>
                <div class="form-group">
                    <div class="form-label"><span>{{ t('bindUsdt.walletAddress') }}</span></div>
                    <div class="input-container">
                        <input type="text" :placeholder="t('bindUsdt.walletAddressPlaceholder')" class="form-input" v-model="formData.walletAddress">
                    </div>
                </div>
                <div class="form-group">
                    <div class="form-label"><span>{{ t('bindUsdt.networkType') }}</span></div>
                    <div class="bank-search-container" ref="networkContainer">
                        <div class="search-input-wrapper">
                            <div class="search-input-content">
                                <div class="selected-bank-icon">
                                    <div class="bank-logo-small">
                                        <svg v-if="formData.networkType === 'TRC20'" viewBox="0 0 24 24" fill="none">
                                            <rect width="24" height="24" rx="4" fill="#E8002A"></rect>
                                            <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-size="7" font-weight="bold">TRC</text>
                                        </svg>
                                        <svg v-else-if="formData.networkType === 'ERC20'" viewBox="0 0 24 24" fill="none">
                                            <rect width="24" height="24" rx="4" fill="#627EEA"></rect>
                                            <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-size="7" font-weight="bold">ERC</text>
                                        </svg>
                                        <svg v-else viewBox="0 0 24 24" fill="none">
                                            <rect width="24" height="24" rx="4" fill="#999999"></rect>
                                            <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-size="7" font-weight="bold">?</text>
                                        </svg>
                                    </div>
                                </div>
                                <input type="text" :placeholder="t('bindUsdt.networkTypePlaceholder')" class="search-input" v-model="formData.networkType" readonly @click="showNetworkOptions = !showNetworkOptions">
                                <div class="search-actions">
                                    <button class="clear-btn" type="button" v-if="formData.networkType" @click="formData.networkType = ''"><span>×</span></button>
                                    <div class="search-icon"><span>▼</span></div>
                                </div>
                            </div>
                        </div>
                        <div class="bank-suggestions" v-show="showNetworkOptions">
                            <div class="suggestion-item" @click="selectNetwork('TRC20')">
                                <div class="suggestion-content">
                                    <div class="bank-logo">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <rect width="24" height="24" rx="4" fill="#E8002A"></rect>
                                            <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-size="7" font-weight="bold">TRC</text>
                                        </svg>
                                    </div>
                                    <div class="bank-info"><span class="bank-code">{{ t('bindUsdt.trc20') }}</span><span class="bank-name">{{ t('bindUsdt.trc20Network') }}</span></div>
                                </div>
                            </div>
                            <div class="suggestion-item" @click="selectNetwork('ERC20')">
                                <div class="suggestion-content">
                                    <div class="bank-logo">
                                        <svg viewBox="0 0 24 24" fill="none">
                                            <rect width="24" height="24" rx="4" fill="#627EEA"></rect>
                                            <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-size="7" font-weight="bold">ERC</text>
                                        </svg>
                                    </div>
                                    <div class="bank-info"><span class="bank-code">{{ t('bindUsdt.erc20') }}</span><span class="bank-name">{{ t('bindUsdt.erc20Network') }}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <div class="form-label"><span>{{ t('bindUsdt.withdrawPassword') }}</span></div>
                    <div class="input-container">
                        <input type="password" :placeholder="t('bindUsdt.withdrawPasswordPlaceholder')" class="form-input" v-model="formData.withdrawPassword">
                    </div>
                </div>
            </div>
        </div>

        <div class="security-notice">
            <div class="notice-card">
                <div class="notice-header"><span class="notice-icon">🔒</span><span class="notice-title">{{ t('bindUsdt.securityNotice') }}</span></div>
                <div class="notice-content">
                    <span class="notice-text">{{ t('bindUsdt.securityText1') }}</span>
                    <span class="notice-text">{{ t('bindUsdt.securityText2') }}</span>
                    <span class="notice-text">{{ t('bindUsdt.securityText3') }}</span>
                </div>
            </div>
        </div>

        <div class="bottom-safe-area"></div>

        <div class="bottom-action">
            <div class="submit-button" :class="{ disabled: isSubmitting }" @click="handleSubmit">
                <div class="button-content">
                    <span class="button-text">{{ isSubmitting ? t('common.submitting') : t('bindUsdt.buttonText') }}</span>
                    <span class="button-subtitle">{{ t('bindUsdt.buttonSubtitle') }}</span>
                </div>
                <div class="button-icon">{{ isSubmitting ? '...' : '✓' }}</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { onClickOutside } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { bindBank, getBankInfo } from '@/api'
import PageHeader from '@/components/PageHeader.vue'

const router = useRouter()
const { t } = useI18n()

const formData = ref({
    walletAddress: '',
    networkType: '',
    withdrawPassword: ''
})

const isSubmitting = ref(false)

const fetchUsdtInfo = async () => {
  try {
    const response = await getBankInfo()
    if (response.data.code === 200) {
      formData.value.walletAddress = response.data.data.cardnum || ''
      formData.value.networkType = response.data.data.bankname || ''
    }
  } catch (error) {
    console.error('获取USDT信息失败:', error)
  }
}

onMounted(() => {
  fetchUsdtInfo()
})

const showNetworkOptions = ref(false)
const networkContainer = ref(null)

onClickOutside(networkContainer, () => {
    showNetworkOptions.value = false
})

const selectNetwork = (network) => {
    formData.value.networkType = network
    showNetworkOptions.value = false
}

const handleSubmit = async () => {
    if (isSubmitting.value) {
        return
    }

    if (!formData.value.walletAddress) {
        ElMessage.warning(t('bindUsdt.errorWalletAddress'))
        return
    }
    if (!formData.value.networkType) {
        ElMessage.warning(t('bindUsdt.errorNetworkType'))
        return
    }
    if (!formData.value.withdrawPassword) {
        ElMessage.warning(t('bindUsdt.errorWithdrawPassword'))
        return
    }

    isSubmitting.value = true

    try {
        const response = await bindBank({
            paypassword: formData.value.withdrawPassword,
            bankname: formData.value.networkType,
            username: 'USDT',
            cardnum: formData.value.walletAddress
        })

        if (response.data.code === 200) {
            ElMessage.success(t('common.submitSuccess'))
            setTimeout(() => {
                router.push('/my')
            }, 2000)
        } else {
            ElMessage.error(response.data.msg || t('common.submitFailed'))
        }
    } catch (error) {
        console.error('绑定USDT失败:', error)
        ElMessage.error(t('common.submitFailed'))
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.form-section {
    margin: 10px
}

.form-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 20px;
    box-shadow: var(--shadow-base);
    margin-bottom: 12px
}

.card-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-color)
}

.card-header .card-title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 6px
}

.card-header .card-subtitle {
    display: block;
    font-size: 11px;
    color: var(--text-secondary)
}

.form-group {
    margin-bottom: 20px
}

.form-group:last-child {
    margin-bottom: 0
}

.form-label {
    margin-bottom: 8px
}

.form-label span {
    font-size: 14px;
    color: var(--text-primary);
    font-weight: 500
}

.bank-search-container {
    position: relative;
    z-index: 100
}

.search-input-wrapper {
    position: relative
}

.search-input-content {
    display: flex;
    align-items: center;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    transition: all .3s ease
}

.search-input-content:focus-within {
    border-color: #667eea;
    background: var(--card-bg);
    box-shadow: 0 0 0 3px #667eea1a
}

.search-input-content .selected-bank-icon {
    padding-left: 12px;
    display: flex;
    align-items: center
}

.search-input-content .selected-bank-icon .bank-logo-small {
    width: 24px;
    height: 24px;
    flex-shrink: 0
}

.search-input-content .selected-bank-icon .bank-logo-small svg {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    box-shadow: 0 1px 3px #0000001a
}

.search-input-content .search-input {
    flex: 1;
    height: 50px;
    padding: 0 12px;
    font-size: 14px;
    color: var(--text-primary);
    background: transparent;
    border: none;
    outline: none
}

.search-input-content .search-input::placeholder {
    color: var(--text-placeholder)
}

.search-input-content .search-actions {
    display: flex;
    align-items: center;
    padding-right: 12px;
    gap: 6px
}

.search-input-content .search-actions .clear-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: var(--bg-tertiary);
    border-radius: 50%;
    color: var(--text-secondary);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all .2s ease
}

.search-input-content .search-actions .clear-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary)
}

.search-input-content .search-actions .search-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 14px;
    color: var(--text-secondary)
}

.bank-suggestions {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: var(--card-bg);
    border-radius: 8px;
    box-shadow: 0 4px 15px #00000026;
    z-index: 101;
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid var(--border-color);
    animation: dropdown-slide-0c7c93a9 .3s ease-out
}

.bank-suggestions .suggestion-item {
    padding: 10px 14px;
    border-bottom: 1px solid var(--border-color);
    transition: all .2s ease;
    cursor: pointer
}

.bank-suggestions .suggestion-item:last-child {
    border-bottom: none
}

.bank-suggestions .suggestion-item:hover,
.bank-suggestions .suggestion-item:active {
    background: #667eea0d
}

.bank-suggestions .suggestion-item .suggestion-content {
    display: flex;
    align-items: center;
    gap: 12px
}

.bank-suggestions .suggestion-item .bank-logo {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center
}

.bank-suggestions .suggestion-item .bank-logo svg {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    box-shadow: 0 1px 4px #0000001a
}

.bank-suggestions .suggestion-item .bank-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px
}

.bank-suggestions .suggestion-item .bank-code {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary)
}

.bank-suggestions .suggestion-item .bank-name {
    font-size: 11px;
    color: var(--text-secondary)
}

.input-container {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    transition: all .3s ease;
    overflow: hidden
}

.input-container:focus-within {
    border-color: #667eea;
    background: var(--card-bg);
    box-shadow: 0 0 0 3px #667eea1a
}

.form-input {
    width: 100%;
    height: 50px;
    padding: 0 14px;
    font-size: 14px;
    color: var(--text-primary);
    background: transparent;
    border: none;
    outline: none
}

.form-input::placeholder {
    color: var(--text-placeholder)
}

.security-notice {
    margin: 10px
}

.notice-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    box-shadow: var(--shadow-base);
    border-left: 4px solid #4CAF50
}

.notice-header {
    display: flex;
    align-items: center;
    margin-bottom: 10px
}

.notice-header .notice-icon {
    font-size: 18px;
    margin-right: 8px
}

.notice-header .notice-title {
    font-size: 14px;
    font-weight: 600;
    color: #4caf50
}

.notice-content .notice-text {
    display: block;
    font-size: 11px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 4px
}

.notice-content .notice-text:last-child {
    margin-bottom: 0
}

.bottom-safe-area {
    height: 80px
}

.bottom-action {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--card-bg);
    padding: 12px 15px 20px;
    border-top: 1px solid var(--border-color);
    z-index: 80
}

.submit-button {
    width: 100%;
    height: 50px;
    background: linear-gradient(135deg,#4caf50,#45a049);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-shadow: 0 4px 15px #4caf504d;
    transition: all .3s ease;
    cursor: pointer
}

.submit-button:active {
    transform: scale(.98)
}

.submit-button.disabled {
    background: #ccc;
    box-shadow: none;
    cursor: not-allowed;
    opacity: .6
}

.submit-button.disabled:active {
    transform: none
}

.submit-button .button-content {
    flex: 1
}

.submit-button .button-content .button-text {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #fff
}

.submit-button .button-content .button-subtitle {
    display: block;
    font-size: 12px;
    color: #ffffffe6;
    margin-top: 2px
}

.submit-button .button-icon {
    font-size: 20px;
    color: #fff;
    font-weight: 600
}

.bank-suggestions::-webkit-scrollbar {
    width: 4px
}

.bank-suggestions::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px
}

.bank-suggestions::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px
}

.bank-suggestions::-webkit-scrollbar-thumb:hover {
    background: #999
}

@keyframes dropdown-slide-0c7c93a9 {
    0% {
        opacity: 0;
        transform: translateY(-5px)
    }

    to {
        opacity: 1;
        transform: translateY(0)
    }
}

@media (max-width: 768px) {
    .card-header {
        margin-bottom: 16px;
        padding-bottom: 8px
    }

    .card-header .card-title {
        font-size: 14px
    }

    .card-header .card-subtitle {
        font-size: 10px
    }

    .form-group {
        margin-bottom: 16px
    }

    .form-label span {
        font-size: 12px
    }

    .search-input-content .search-input {
        height: 44px;
        padding: 0 10px;
        font-size: 12px
    }

    .search-input-content .selected-bank-icon {
        padding-left: 10px
    }

    .search-input-content .selected-bank-icon .bank-logo-small {
        width: 20px;
        height: 20px
    }

    .search-input-content .search-actions {
        padding-right: 10px;
        gap: 4px
    }

    .search-input-content .search-actions .clear-btn {
        width: 20px;
        height: 20px;
        font-size: 14px
    }

    .search-input-content .search-actions .search-icon {
        width: 20px;
        height: 20px;
        font-size: 12px
    }

    .bank-suggestions {
        max-height: 250px
    }

    .bank-suggestions .suggestion-item {
        padding: 8px 12px
    }

    .bank-suggestions .suggestion-item .bank-logo {
        width: 28px;
        height: 28px
    }

    .bank-suggestions .suggestion-item .bank-code {
        font-size: 12px
    }

    .bank-suggestions .suggestion-item .bank-name {
        font-size: 10px
    }

    .form-input {
        height: 44px;
        padding: 0 12px;
        font-size: 12px
    }

    .notice-card {
        padding: 14px
    }

    .notice-header {
        margin-bottom: 8px
    }

    .notice-header .notice-icon {
        font-size: 16px
    }

    .notice-header .notice-title {
        font-size: 12px
    }

    .notice-text {
        font-size: 10px
    }

    .bottom-action {
        padding: 10px 12px 16px
    }

    .submit-button {
        height: 44px;
        padding: 0 16px
    }

    .submit-button .button-content .button-text {
        font-size: 14px
    }

    .submit-button .button-content .button-subtitle {
        font-size: 11px
    }

    .submit-button .button-icon {
        font-size: 18px
    }
}
</style>
